import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ProvisioningScreen } from './ProvisioningScreen';
import { TuesdayOSLogo } from '@/components/icons/TuesdayOSLogo';
import { StepAboutYou } from './StepAboutYou';
import { StepContact } from './StepContact';
import { StepCompany } from './StepCompany';
import { StepPhone } from './StepPhone';
import { StepBusinessType } from './StepBusinessType';
import { StepAppInterests } from './StepAppInterests';
import { StepChatChannels } from './StepChatChannels';
import { StepChatVolume } from './StepChatVolume';
import { StepSellingCategory } from './StepSellingCategory';
import { StepSellingChannels } from './StepSellingChannels';
import { StepPosType } from './StepPosType';
import { StepPosBranches } from './StepPosBranches';
import { StepCrmTeamSize } from './StepCrmTeamSize';
import { StepCrmCurrentTool } from './StepCrmCurrentTool';
import { StepDesktopHandoff } from './StepDesktopHandoff';
import { StepAuthMethod } from './StepAuthMethod';
import { useRegistrationStore, type QualificationData } from '@/lib/store/registrationStore';
import { useAuthStore } from '@/lib/store/authStore';
import { sanitizeCompanyDocname } from '@/lib/store/companyDocname';
import { cpRegister, cpRegisterFromLine, cpGoogleOneTap, setCPToken } from '@/lib/api/controlPlaneClient';
import { ChevronLeft } from 'lucide-react';
import posthog from 'posthog-js';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

const BRAND_SUFFIXES = ['os', 'business', 'จัดการธุรกิจ', 'chat', 'รวมแชท', 'pos', 'ขายหน้าร้าน', 'rewards', 'สะสมแต้ม'];

/**
 * Conversion Value Matrix — maps intent + qualification answers to a dollar value.
 * Higher value = higher-intent lead for Google Ads bidding.
 *
 * Base values per intent:
 *   chat: $5, selling: $10, pos: $15, crm: $10, generic: $2
 *
 * Multipliers from qualification answers:
 *   Volume/scale signals (high volume, many branches, large team) → 2-3x
 *   Multi-channel signals (3+ channels) → 1.5x
 */
function computeConversionValue(intent: string | null, qd: QualificationData): number {
  if (!intent) return 2;

  let base = 2;
  let multiplier = 1;

  switch (intent) {
    case 'chat': {
      base = 5;
      // Volume multiplier
      if (qd.chatVolume === 'over_500') multiplier = 3;
      else if (qd.chatVolume === '200_500') multiplier = 2;
      else if (qd.chatVolume === '50_200') multiplier = 1.5;
      // Multi-channel bonus
      if (qd.chatChannels.length >= 3) multiplier *= 1.5;
      break;
    }
    case 'selling': {
      base = 10;
      // Multi-channel bonus
      if (qd.sellingChannels.length >= 3) multiplier = 2;
      else if (qd.sellingChannels.length >= 2) multiplier = 1.5;
      break;
    }
    case 'pos': {
      base = 15;
      // Branch multiplier
      if (qd.posBranches === 'over_20') multiplier = 3;
      else if (qd.posBranches === '6_20') multiplier = 2;
      else if (qd.posBranches === '2_5') multiplier = 1.5;
      break;
    }
    case 'contacts':
    case 'crm': {
      base = 10;
      // Team size multiplier
      if (qd.crmTeamSize === 'over_20') multiplier = 3;
      else if (qd.crmTeamSize === '6_20') multiplier = 2;
      else if (qd.crmTeamSize === '2_5') multiplier = 1.5;
      break;
    }
  }

  return Math.round(base * multiplier);
}

/**
 * Build the step sequence based on intent and mode.
 * Returns an array of step IDs that the wizard will walk through.
 *
 * Generic (no intent):
 *   about_you → contact → company → phone → business_type → app_interests → desktop_handoff
 *
 * With intent (e.g. 'chat'):
 *   about_you → contact → company → phone → [chat_channels, chat_volume] → business_type → app_interests → desktop_handoff
 *
 * LINE mode skips about_you and contact (user already authed via LINE).
 * Desktop handoff only shows in LINE mode (mobile users who need desktop access).
 */
/** Detect mobile device via screen width (matches Tailwind md: breakpoint) */
function getIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

function buildStepSequence(intent: string | null, isLineMode: boolean, isGoogleMode: boolean, isMobile: boolean): string[] {
  const common = isLineMode
    ? ['company', 'phone']
    : isGoogleMode
      ? ['auth_method', 'company', 'phone']
      : ['auth_method', 'about_you', 'contact', 'company', 'phone'];

  let intentSteps: string[] = [];
  switch (intent) {
    case 'chat':
      intentSteps = ['chat_channels', 'chat_volume'];
      break;
    case 'selling':
      intentSteps = ['selling_channels'];
      break;
    case 'pos':
      intentSteps = ['pos_branches'];
      break;
    case 'contacts':
    case 'crm':
      intentSteps = ['crm_team_size', 'crm_current_tool'];
      break;
  }

  const tail = isLineMode && isMobile
    ? ['business_type', 'app_interests', 'desktop_handoff']
    : ['business_type', 'app_interests'];

  return [...common, ...intentSteps, ...tail];
}

/** Pack qualification answers into a flat object for the CP payload */
function packQualificationData(qd: QualificationData, intent: string | null): Record<string, unknown> | undefined {
  if (!intent) return undefined;
  const data: Record<string, unknown> = {};
  switch (intent) {
    case 'chat':
      if (qd.chatChannels.length > 0) data.channels = qd.chatChannels;
      if (qd.chatVolume) data.volume = qd.chatVolume;
      break;
    case 'selling':
      if (qd.sellingCategory) data.category = qd.sellingCategory;
      if (qd.sellingChannels.length > 0) data.channels = qd.sellingChannels;
      break;
    case 'pos':
      if (qd.posType) data.store_type = qd.posType;
      if (qd.posBranches) data.branches = qd.posBranches;
      break;
    case 'contacts':
    case 'crm':
      if (qd.crmTeamSize) data.team_size = qd.crmTeamSize;
      if (qd.crmCurrentTool) data.current_tool = qd.crmCurrentTool;
      break;
  }
  return Object.keys(data).length > 0 ? data : undefined;
}

export function RegistrationWizard() {
  const navigate = useNavigate();
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const {
    currentStep,
    formData,
    qualificationData,
    nextStep,
    prevStep,
    setError,
    setSubmitting,
    isSubmitting,
    reset,
    hasHydrated,
    intent,
    // LINE mode
    isLineMode,
    lineUserId,
    linePictureUrl,
    clearLineMode,
    // Google mode
    isGoogleMode,
    googleCredential,
    setGoogleMode,
    clearGoogleMode,
  } = useRegistrationStore();

  // Build step sequence based on intent (isMobile checked once at mount)
  const [isMobile] = useState(getIsMobile);
  const stepSequence = useMemo(() => buildStepSequence(intent, isLineMode, isGoogleMode, isMobile), [intent, isLineMode, isGoogleMode, isMobile]);
  const totalSteps = stepSequence.length;
  const currentStepId = stepSequence[currentStep] || stepSequence[0];

  // Brand suffix cycling (same as website navbar)
  const [suffixIndex, setSuffixIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setSuffixIndex((i) => (i + 1) % BRAND_SUFFIXES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  const suffix = BRAND_SUFFIXES[suffixIndex];

  // G11: Invalid state recovery - if LINE mode but no lineUserId, reset
  useEffect(() => {
    if (hasHydrated && isLineMode && !lineUserId) {
      clearLineMode();
      reset();
    }
  }, [hasHydrated, isLineMode, lineUserId, clearLineMode, reset]);

  // G-mode: Invalid state recovery - if Google mode but no credential, reset
  useEffect(() => {
    if (hasHydrated && isGoogleMode && !googleCredential) {
      clearGoogleMode();
    }
  }, [hasHydrated, isGoogleMode, googleCredential, clearGoogleMode]);

  // Track registration step views for funnel analysis (Plan 424)
  useEffect(() => {
    if (!hasHydrated) return;
    if (currentStepId && posthog.__loaded) {
      posthog.capture('registration_step_viewed', {
        step: currentStep,
        step_name: currentStepId,
        mode: isLineMode ? 'line' : isGoogleMode ? 'google' : 'email',
        intent: intent || undefined,
      });
    }
  }, [currentStep, hasHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Provisioning screen state
  const [showProvisioning, setShowProvisioning] = useState(false);
  const [provisioningReady, setProvisioningReady] = useState(false);
  const pendingNavigate = useRef(false);

  // Check hydration before rendering to prevent flash of wrong step
  if (!hasHydrated) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t.loading}</div>
      </div>
    );
  }

  /** Common post-registration handler */
  const handlePostRegistration = (
    response: { user_id: string; tenant_id: string; access_token: string; refresh_token: string; server_url: string; company: string; is_provisioned: boolean; storefront_url?: string },
    method: 'email' | 'line' | 'google',
    qData: Record<string, unknown> | undefined,
    conversionValue: number,
  ) => {
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    setCPToken(response.access_token, expiresAt, response.refresh_token);

    useAuthStore.setState({
      isAuthenticated: true,
      username: response.user_id,
      cpToken: response.access_token,
      cpTokenExpiresAt: expiresAt,
      currentTenant: response.tenant_id,
      tenantServerUrl: response.server_url,
      tenantCompany: sanitizeCompanyDocname(response.company, response.tenant_id),
      isProvisioned: response.is_provisioned,
      storefrontUrl: response.storefront_url || null,
    });

    if (import.meta.env.VITE_POSTHOG_KEY) {
      // Capture anonymous distinct_id BEFORE identify() switches it.
      // This links the LP visitor (who has the GCLID from Google Ads click)
      // with the newly registered user — so PostHog can attribute the full journey.
      const anonId = posthog.get_distinct_id();

      const identifyProps: Record<string, unknown> = {};
      // Use real email for ECL if provided, otherwise registration email
      const emailForIdentify = formData.realEmail || (method === 'email' ? formData.email : undefined);
      if (emailForIdentify) identifyProps.email = emailForIdentify;
      posthog.identify(response.user_id, Object.keys(identifyProps).length > 0 ? identifyProps : undefined);

      // Merge anonymous LP visitor with registered user (Epic 08 chat attribution)
      if (anonId && anonId !== response.user_id) {
        posthog.alias(response.user_id, anonId);
      }

      posthog.capture('signup_completed', {
        method,
        tenant_id: response.tenant_id,
        intent: intent || undefined,
        qualification_data: qData,
        conversion_value: conversionValue,
      });

      // Fire separate email_collected event for funnel analysis
      if (formData.realEmail) {
        posthog.people.set({ $email: formData.realEmail });
        posthog.capture('email_collected', {
          source: 'desktop_handoff',
          tenant_id: response.tenant_id,
        });
      }
    }
  };

  // Normal registration submit (email/password flow)
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setShowProvisioning(true);
    setProvisioningReady(false);

    try {
      const qData = packQualificationData(qualificationData, intent);
      const conversionValue = computeConversionValue(intent, qualificationData);
      const response = await cpRegister({
        email: formData.email,
        password: formData.password,
        tenant_name: formData.companyName,
        first_name: formData.firstName || undefined,
        last_name: formData.lastName || undefined,
        phone_number: formData.phoneNumber || undefined,
        business_type: formData.businessType || undefined,
        app_interests: formData.appInterests.length > 0 ? formData.appInterests : undefined,
        intent: intent || undefined,
        qualification_data: qData ? JSON.stringify(qData) : undefined,
        real_email: formData.realEmail || undefined,
      });

      handlePostRegistration(response, 'email', qData, conversionValue);
      reset();
      pendingNavigate.current = true;
      setProvisioningReady(true);
    } catch (err) {
      setShowProvisioning(false);
      setError(err instanceof Error ? err.message : t.registrationFailed);
    } finally {
      setSubmitting(false);
    }
  };

  // LINE mode registration submit
  const handleLineSubmit = async () => {
    if (!lineUserId) {
      setError('LINE user ID missing');
      return;
    }

    setSubmitting(true);
    setError(null);
    setShowProvisioning(true);
    setProvisioningReady(false);

    try {
      const qData = packQualificationData(qualificationData, intent);
      const conversionValue = computeConversionValue(intent, qualificationData);
      const response = await cpRegisterFromLine({
        line_user_id: lineUserId,
        first_name: formData.firstName,
        last_name: formData.lastName || undefined,
        tenant_name: formData.companyName,
        phone_number: formData.phoneNumber || undefined,
        business_type: formData.businessType || undefined,
        app_interests: formData.appInterests.length > 0 ? formData.appInterests : undefined,
        line_picture_url: linePictureUrl || undefined,
        intent: intent || undefined,
        qualification_data: qData ? JSON.stringify(qData) : undefined,
        real_email: formData.realEmail || undefined,
      });

      handlePostRegistration(response, 'line', qData, conversionValue);
      clearLineMode();
      reset();
      pendingNavigate.current = true;
      setProvisioningReady(true);
    } catch (err) {
      setShowProvisioning(false);
      setError(err instanceof Error ? err.message : t.registrationFailed);
    } finally {
      setSubmitting(false);
    }
  };


  // Google mode registration submit (deferred credential + profile data)
  const handleGoogleSubmit = async () => {
    if (!googleCredential) {
      setError('Google credential missing');
      return;
    }

    setSubmitting(true);
    setError(null);
    setShowProvisioning(true);
    setProvisioningReady(false);

    try {
      const qData = packQualificationData(qualificationData, intent);
      const conversionValue = computeConversionValue(intent, qualificationData);
      const response = await cpGoogleOneTap({
        credential: googleCredential,
        tenant_name: formData.companyName || undefined,
        phone_number: formData.phoneNumber || undefined,
        business_type: formData.businessType || undefined,
        app_interests: formData.appInterests.length > 0 ? formData.appInterests : undefined,
        intent: intent || undefined,
        qualification_data: qData ? JSON.stringify(qData) : undefined,
      });

      handlePostRegistration(response, 'google', qData, conversionValue);
      clearGoogleMode();
      reset();
      pendingNavigate.current = true;
      setProvisioningReady(true);
    } catch (err) {
      setShowProvisioning(false);
      setError(err instanceof Error ? err.message : t.registrationFailed);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    if (currentStep <= 0) {
      if (isLineMode) {
        clearLineMode();
        reset();
        navigate('/login');
      }
      return;
    }
    // If going back to auth_method step from Google mode, clear Google state
    const prevStepId = stepSequence[currentStep - 1];
    if (prevStepId === 'auth_method' && isGoogleMode) {
      clearGoogleMode();
    }
    prevStep();
  };

  // Determine which submit handler to use
  const submitHandler = isLineMode ? handleLineSubmit : isGoogleMode ? handleGoogleSubmit : handleSubmit;

  const renderStep = () => {
    switch (currentStepId) {
      case 'auth_method':
        return (
          <StepAuthMethod
            onGoogleCredential={(credential) => {
              setGoogleMode(credential);
            }}
            onEmailFallback={nextStep}
          />
        );
      case 'about_you':
        return <StepAboutYou onNext={nextStep} />;
      case 'contact':
        return <StepContact onNext={nextStep} />;
      case 'company':
        return <StepCompany onNext={nextStep} />;
      case 'phone':
        return isLineMode
          ? <StepPhone onNext={nextStep} />
          : <StepPhone onNext={nextStep} onSkip={nextStep} />;  // Google & email: phone is skippable
      // Intent-specific qualification steps
      case 'chat_channels':
        return <StepChatChannels onNext={nextStep} />;
      case 'chat_volume':
        return <StepChatVolume onNext={nextStep} />;
      case 'selling_category':
        return <StepSellingCategory onNext={nextStep} />;
      case 'selling_channels':
        return <StepSellingChannels onNext={nextStep} />;
      case 'pos_type':
        return <StepPosType onNext={nextStep} />;
      case 'pos_branches':
        return <StepPosBranches onNext={nextStep} />;
      case 'crm_team_size':
        return <StepCrmTeamSize onNext={nextStep} />;
      case 'crm_current_tool':
        return <StepCrmCurrentTool onNext={nextStep} />;
      // Common tail steps
      case 'business_type':
        return <StepBusinessType onNext={nextStep} onSkip={nextStep} />;
      case 'app_interests':
        // If no desktop_handoff step follows, app_interests is the final step
        return stepSequence.includes('desktop_handoff')
          ? <StepAppInterests onSubmit={nextStep} isSubmitting={false} />
          : <StepAppInterests onSubmit={submitHandler} isSubmitting={isSubmitting} />;
      case 'desktop_handoff':
        return <StepDesktopHandoff onSubmit={submitHandler} onSkip={submitHandler} isSubmitting={isSubmitting} />;
      default:
        return null;
    }
  };

  const showBackButton = isLineMode ? true : currentStep > 0;

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">

      {/* Provisioning overlay — Apple Hello style */}
      <AnimatePresence>
        {showProvisioning && (
          <ProvisioningScreen
            isReady={provisioningReady}
            onComplete={() => {
              setShowProvisioning(false);
              if (pendingNavigate.current) {
                pendingNavigate.current = false;
                navigate('/');
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Content — vertically centered on first step, top-aligned on others */}
      <div className={`px-6 pb-8 md:pb-12 ${showBackButton ? 'pt-6 md:pt-12' : 'flex-1 flex items-center'}`}>
        <div className="w-full max-w-sm md:mx-auto">
          {/* Back + step indicator — hidden on first step */}
          {showBackButton && (
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                {t.back}
              </button>
              <span className="text-sm text-muted-foreground">
                {currentStep + 1} / {totalSteps}
              </span>
            </div>
          )}

          {/* Logo + Brand */}
          <div className="flex items-center gap-2.5 mb-4 md:justify-center">
            <motion.div
              initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              whileHover={{ rotate: 360, transition: { duration: 0.8, ease: [0.25, 0.4, 0.25, 1] } }}
              transition={{ duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
            >
              <TuesdayOSLogo className="w-9 h-9" />
            </motion.div>
            <motion.span
              className="leading-none tracking-[-0.03em] text-foreground"
              style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 22, fontWeight: 500 }}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
            >
              <span className="relative inline-flex items-baseline">tuesday<AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={suffix}
                  className="text-[#0051FD]"
                  style={{
                    fontFamily: /[ก-๙]/.test(suffix) ? "'IBM Plex Sans Thai', system-ui, sans-serif" : undefined,
                    fontSize: /[ก-๙]/.test(suffix) ? '0.9em' : undefined,
                    lineHeight: 0,
                    whiteSpace: 'nowrap',
                  }}
                  initial={{ opacity: 0, y: 6, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                  transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
                >
                  {suffix === 'os' ? suffix : `.${suffix}`}
                </motion.span>
              </AnimatePresence></span>
            </motion.span>
          </div>

          {/* Step content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Login link */}
          {!isLineMode && !isGoogleMode && (
            <p className="pt-4 text-sm text-muted-foreground md:text-center">
              {t.alreadyHaveAccount}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t.signIn}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
